import React, { forwardRef, useEffect, useImperativeHandle } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';
import { boolean, object, string } from 'yup';

import { Add as AddIcon } from '@mui/icons-material';
import { Box, Button, FormControlLabel, Stack, Switch, Typography } from '@mui/material';

import { Input } from '~/components/Common/Forms/fields';

import { CreatePromoTagPayload, PromoRole } from '~/services/PromoService';

import PromoRoleSelector from '../PromoRoles/PromoRoleSelector';

type PromoTagFormData = {
  dept_tag: string;
  category_tag: string;
  sub_category_tag: string;
  isActive: boolean;
  roleIdRequired: string;
};

interface Props {
  promoRoles: Array<PromoRole>;
  onSubmit: (data: CreatePromoTagPayload) => Promise<void>;
  isLoading?: boolean;
  initialValues?: {
    dept_tag: string;
    category_tag: string;
    sub_category_tag: string;
    isActive: boolean;
    roleIdRequired: string;
  };
  resetForm?: () => void;
}

const validationSchema = object({
  dept_tag: string().required('Department tag is required'),
  category_tag: string().required('Category tag is required'),
  sub_category_tag: string().required('Sub category tag is required'),
  isActive: boolean(),
  roleIdRequired: string(),
});

const PromoTagAddForm = forwardRef<{ reset: () => void }, Props>(
  ({ promoRoles, onSubmit, isLoading = false, initialValues, resetForm }, ref) => {
    const {
      control,
      handleSubmit,
      formState: { errors, isValid },
      reset,
    } = useForm<PromoTagFormData>({
      defaultValues: initialValues || {
        dept_tag: '',
        category_tag: '',
        sub_category_tag: '',
        isActive: true,
        roleIdRequired: '',
      },
      resolver: yupResolver(validationSchema),
      mode: 'onChange',
    });

    // Expose reset function to parent component
    useImperativeHandle(ref, () => ({
      reset: () => {
        reset({
          dept_tag: '',
          category_tag: '',
          sub_category_tag: '',
          isActive: true,
          roleIdRequired: '',
        });
      },
    }));

    const handleFormSubmit = async (data: PromoTagFormData) => {
      const payload: CreatePromoTagPayload = {
        departmentTag: data.dept_tag,
        categoryTag: data.category_tag,
        subCategoryTag: data.sub_category_tag,
        isActive: data.isActive,
        roleIdRequired: data.roleIdRequired || undefined,
      };

      await onSubmit(payload);
      reset();
    };

    // Update form when initialValues change
    useEffect(() => {
      if (initialValues) {
        reset(initialValues);
      }
    }, [initialValues, reset]);

    return (
      <Box component="form" onSubmit={handleSubmit(handleFormSubmit)}>
        <Stack direction="column" spacing={2}>
          <Typography variant="h6">{'Add New Promo Tag'}</Typography>
          <Typography variant="caption">
            Promo tags are used to categorize promo codes and classify them for reporting, filtering and management.
            Select the existing promo tags above to populate existing tag values
          </Typography>

          <Stack direction="row" spacing={2}>
            <Input
              control={control}
              name="dept_tag"
              label="Department Tag"
              required
              fullWidth
              helperText="Enter the department tag (e.g., Consumer Marketing, Sales, Marketing)"
            />
            <Input
              control={control}
              name="category_tag"
              label="Category Tag"
              required
              fullWidth
              helperText="Enter the category tag (e.g., Goodwill, Brand Marketing, etc)"
            />
            <Input
              control={control}
              name="sub_category_tag"
              label="Sub Category Tag"
              required
              fullWidth
              helperText="Enter the sub category tag (e.g., Quick Resolution, Paid Referral, etc)"
            />
          </Stack>

          <Stack direction="row" spacing={2} alignItems="center">
            <Controller
              name="isActive"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={<Switch checked={field.value} onChange={field.onChange} disabled={isLoading} />}
                  label="Active"
                />
              )}
            />

            <Box sx={{ flex: 1 }}>
              <Controller
                name="roleIdRequired"
                control={control}
                render={({ field }) => (
                  <PromoRoleSelector
                    id="roleIdRequired"
                    label="Role Required (Optional)"
                    promoRoles={promoRoles}
                    selectedRoleId={field.value}
                    onChange={field.onChange}
                    clearOnBlur={true}
                  />
                )}
              />
            </Box>
          </Stack>

          <Button
            type="submit"
            variant="contained"
            startIcon={<AddIcon />}
            disabled={!isValid || isLoading}
            sx={{ maxWidth: '200px' }}
          >
            {isLoading ? 'Adding...' : 'Add Promo Tag'}
          </Button>
        </Stack>
      </Box>
    );
  },
);

PromoTagAddForm.displayName = 'PromoTagAddForm';

export default PromoTagAddForm;
